/**
 * The fighter state machine: timers, intent resolution, and move progression.
 * ARCHITECTURE.md §7.1 (states), §7.5 (move data). Three-free.
 *
 * Design principles this file exists to enforce:
 *  - Every state change goes through `enterState`, which validates against the declared
 *    transition table. An illegal transition is a thrown error in dev, not a silent glitch.
 *  - A move's behaviour comes entirely from its data. Nothing here special-cases a move id.
 *  - Commitment is real: a move cannot be cancelled outside its authored cancel window, and
 *    cancels can only reach the moves the string tree declares.
 */

import {
  FIXED_STEP, INPUT_BUFFER_TICKS, THROW_BREAK_TICKS, GROUND_Y, clamp, f60,
} from '../core/index.js';
import { S, isNonCombatState } from './states.js';
import { isLegalTransition } from './transitions.js';
import { refreshDerived, resetCombo, logAction } from './fighter-state.js';
import { findMove } from './notation.js';
import { addMeter } from './damage.js';

/** Ticks a fighter stays in LANDING after a normal descent. */
const LAND_TICKS = f60(4);
const HARD_LAND_TICKS = f60(14);
/** Ticks on the floor before wake-up options become available. */
const GROUND_TICKS_SOFT = f60(20);
const GROUND_TICKS_HARD = f60(34);
/** Invulnerability granted on wake-up, per option. */
const WAKEUP_INVULN = { neutral: f60(3), attack: f60(6), roll: f60(8) };
/** Ticks of the crouch transition that still count as standing for hit-level purposes. */
const GUARD_RECOVERY_TICKS = f60(3);

/**
 * Transitions a fighter to a new state, emitting `fighter:state`.
 * @param {object} f FighterState
 * @param {string} next
 * @param {object} ctx
 * @param {object} emitter CombatEmitter
 * @param {string} [moveId]
 */
export function enterState(f, next, ctx, emitter, moveId = '') {
  const from = f.state;
  if (from === next) {
    // Re-entry resets the state timer (e.g. re-pressing forward mid-walk) but is not an event.
    f.stateTick = 0;
    f.stateEnterTick = ctx.time.tick;
    return;
  }
  if (!isLegalTransition(from, next)) {
    const msg = `illegal transition ${from} -> ${next} (fighter ${f.index}, move "${moveId || f.moveId}")`;
    if (ctx.flags.dev || ctx.flags.deterministic) throw new Error(`[combat] ${msg}`);
    ctx.log.warnOnce(`trans:${from}>${next}`, `[combat] ${msg}`);
    return;
  }
  f.prevState = from;
  f.state = next;
  f.stateTick = 0;
  f.stateEnterTick = ctx.time.tick;
  refreshDerived(f);
  emitter.state(f.index, from, next, moveId || f.moveId);
}

/**
 * Hard-sets a state, bypassing the transition table.
 *
 * ONLY for resets: round reset, training-mode teleports, replay seeking. These are not gameplay
 * transitions — the fighter is being reconstructed, not moved — so validating them against the
 * adjacency table would reject legitimate resets (e.g. guard-crouch -> idle) and mask real bugs
 * by forcing the table to be loosened. Every in-match transition must still go through
 * `enterState`.
 */
export function forceState(f, next, ctx, emitter) {
  const from = f.state;
  f.prevState = from;
  f.state = next;
  f.stateTick = 0;
  f.stateEnterTick = ctx.time.tick;
  refreshDerived(f);
  if (from !== next && emitter) emitter.state(f.index, from, next, '');
}

/**
 * Begins a move. Clears per-move hit flags so a re-used move cannot inherit the previous
 * attempt's "already hit" bookkeeping.
 */
export function startMove(f, move, ctx, emitter) {
  f.moveId = move.id;
  f.move = move;
  f.moveTick = 0;
  f.moveHasHit = false;
  f.hitboxUsed.fill(0);
  f.bufferedMoveId = '';
  f.stringDelayTicks = 0;
  if (!move.followUpOf) f.stringRootId = move.id;

  if (move.meterCost > 0) f.meter = Math.max(0, f.meter - move.meterCost);
  if (move.stanceEnter) {
    f.stance = move.stanceEnter;
    f.stanceTicks = 0;
  }

  // Armor and invulnerability windows are armed here and consumed by hit resolution.
  f.armorHitsLeft = move.armor.hits;
  f.armorDamageMul = move.armor.damageMul;
  f.invulnLevels.length = 0;
  for (const lv of move.invuln.levels) f.invulnLevels.push(lv);

  // A parry is not an attack and must never pass through attack-startup — it goes straight to
  // its active window, which is what makes it a 5-frame read rather than a slow poke.
  if (move.category === 'defence') {
    emitter.moveStart(f.index, move);
    f.actionLockTicks = f60(Math.max(1, f.stats.parryWindowFrames));
    f.parryTicks = f60(Math.max(1, f.stats.parryWindowFrames));
    f.parrySucceeded = false;
    enterState(f, S.PARRY_ACTIVE, ctx, emitter, move.id);
    f.move = null;
    f.moveId = '';
    if (move.sfx.swing) emitter.cue(move.sfx.swing, f.x, f.y + 1.2, f.z, 0.6, 1, 0);
    return;
  }

  const target = move.airborneAttacker ? S.AIR_ATTACK
    : move.category === 'throw' || move.category === 'command-throw' ? S.THROW_ATTEMPT
    : move.category === 'finisher' ? S.FINISHER_ATTACKER
    : move.category === 'stance' ? S.STANCE_SHIFT
    : S.ATTACK_STARTUP;

  enterState(f, target, ctx, emitter, move.id);
  emitter.moveStart(f.index, move);
  if (move.sfx.swing) {
    emitter.cue(move.sfx.swing, f.x, f.y + 1.2, f.z, 0.8, 1, 0);
  }
}

/** Clears the active move and returns the fighter to a neutral-ish state. */
export function endMove(f, ctx, emitter, next = S.IDLE) {
  const finished = f.moveId;
  f.moveId = '';
  f.move = null;
  f.moveTick = 0;
  f.moveHasHit = false;
  f.hitboxUsed.fill(0);
  f.armorHitsLeft = 0;
  f.invulnLevels.length = 0;
  if (finished) logAction(f, ctx.time.tick, finished, 'end');
  enterState(f, next, ctx, emitter);
}

// ---------------------------------------------------------------------------
// Per-tick timers
// ---------------------------------------------------------------------------

/**
 * Decrements every countdown on the fighter and handles the transitions those countdowns
 * own (hitstun expiry, knockdown timing, guard regeneration, stance upkeep).
 *
 * Returns true when the fighter is frozen by hitstop and the rest of the tick must be skipped.
 */
export function tickTimers(f, step, ctx, emitter) {
  f.aliveTicks++;

  if (f.frozenTicks > 0) {
    f.frozenTicks--;
    return true;
  }

  f.stateTick++;
  f.poseTick++;

  if (f.invulnTicks > 0) f.invulnTicks--;
  if (f.actionLockTicks > 0) f.actionLockTicks--;
  if (f.stance) f.stanceTicks++;
  // The parry window must expire on its own. Leaving it armed would let a parry fire from
  // guard-recovery — long after the read was over.
  if (f.parryTicks > 0) f.parryTicks--;

  // Guard regenerates only while not actively guarding, so sustained pressure is a real threat.
  if (!f.guarding && f.guardMeter < f.guardMax) {
    f.guardMeter = Math.min(f.guardMax, f.guardMeter + f.stats.guardRegenPerSecond * step);
  }

  // --- blockstun ---
  if (f.blockstunTicks > 0) {
    f.blockstunTicks--;
    if (f.blockstunTicks === 0) {
      enterState(f, f.crouching ? S.GUARD_CROUCH : S.GUARD_STAND, ctx, emitter);
    }
    return false;
  }

  // --- hitstun ---
  if (f.hitstunTicks > 0) {
    f.hitstunTicks--;
    if (f.hitstunTicks === 0) resolveHitstunExit(f, ctx, emitter);
    return false;
  }

  // --- throw break window (victim side) ---
  if (f.throwBreakTicks > 0) f.throwBreakTicks--;

  // --- wall splat / slump ---
  if (f.state === S.WALL_SPLAT) {
    if (f.wallStunTicks > 0) f.wallStunTicks--;
    if (f.wallStunTicks === 0) enterState(f, S.WALL_SLUMP, ctx, emitter);
    return false;
  }
  if (f.state === S.WALL_SLUMP) {
    if (f.stateTick >= f60(16)) beginKnockdownGround(f, ctx, emitter, false);
    return false;
  }

  // --- knockdown ---
  if (f.state === S.KNOCKDOWN_FALL) {
    // Test the actual position, NOT the derived `grounded` flag: KNOCKDOWN_FALL is classified as
    // an airborne state for hurtbox purposes, so `f.grounded` is false for its whole duration and
    // using it here would leave the fighter falling forever.
    if (f.y <= GROUND_Y + 1e-4 && f.vy <= 0) beginKnockdownGround(f, ctx, emitter, f.knockdownHard);
    return false;
  }
  if (f.state === S.KNOCKDOWN_GROUND) {
    f.groundTicks++;
    return false;
  }

  // --- landing ---
  if (f.state === S.LANDING && f.stateTick >= LAND_TICKS) {
    enterState(f, S.IDLE, ctx, emitter);
  } else if (f.state === S.HARD_LANDING && f.stateTick >= HARD_LAND_TICKS) {
    enterState(f, S.IDLE, ctx, emitter);
  }

  // --- fixed-duration recovery states ---
  if (f.actionLockTicks === 0) {
    switch (f.state) {
      case S.DASH_FWD:
        enterState(f, S.IDLE, ctx, emitter);
        break;
      case S.DASH_BACK:
        f.actionLockTicks = f60(4);
        enterState(f, S.BACKDASH_RECOVERY, ctx, emitter);
        break;
      case S.BACKDASH_RECOVERY:
      case S.SIDESTEP_RECOVERY:
      case S.GUARD_RECOVERY:
      case S.CROUCH_EXIT:
      case S.THROW_BROKEN:
      case S.THROW_TECH:
      case S.PARRY_SUCCESS:
      case S.WAKEUP_NEUTRAL:
      case S.WAKEUP_ROLL:
        enterState(f, S.IDLE, ctx, emitter);
        break;
      case S.SIDESTEP_LEFT:
      case S.SIDESTEP_RIGHT:
        f.actionLockTicks = f60(f.stats.sidestepRecoveryFrames);
        enterState(f, S.SIDESTEP_RECOVERY, ctx, emitter);
        break;
      case S.CROUCH_ENTER:
        enterState(f, S.CROUCH, ctx, emitter);
        break;
      case S.PARRY_ACTIVE:
        // Whiffed parry: the window closes and the fighter is stuck in recovery.
        f.parryTicks = 0;
        f.actionLockTicks = f60(f.stats.parryRecoveryFrames);
        enterState(f, S.GUARD_RECOVERY, ctx, emitter);
        break;
      default:
        break;
    }
  }

  return false;
}

/** Chooses what happens when hitstun runs out: stand, crouch, or fall over. */
function resolveHitstunExit(f, ctx, emitter) {
  // States with their own countdown own their own exit. A wall splat, slump, knockdown or
  // grapple must not be short-circuited back to idle just because the hitstun that produced
  // it expired.
  if (f.state === S.WALL_SPLAT || f.state === S.WALL_SLUMP || f.state === S.KNOCKDOWN_FALL
      || f.state === S.KNOCKDOWN_GROUND || f.state === S.THROWN || f.state === S.FINISHER_VICTIM) {
    return;
  }
  if (f.state === S.HITSTUN_AIR || f.state === S.HITSTUN_LAUNCH) {
    // Airborne hitstun does not simply expire — the fighter falls until they land. Position, not
    // the derived `grounded` flag, decides that (these states are classified airborne).
    f.hitstunTicks = 1;
    if (f.y <= GROUND_Y + 1e-4 && f.vy <= 0) {
      f.hitstunTicks = 0;
      beginKnockdown(f, ctx, emitter, true, true);
    }
    return;
  }
  if (f.state === S.HITSTUN_CROUCH) {
    enterState(f, S.CROUCH, ctx, emitter);
    return;
  }
  enterState(f, S.IDLE, ctx, emitter);
}

export function beginKnockdown(f, ctx, emitter, hard, faceUp) {
  f.knockdownHard = hard;
  f.faceUp = faceUp;
  f.hitstunTicks = 0;
  f.blockstunTicks = 0;
  if (f.state !== S.KNOCKDOWN_FALL) enterState(f, S.KNOCKDOWN_FALL, ctx, emitter);
  emitter.knockdown(f.index, hard, faceUp, f.headTowardOpponent, f.x, f.y, f.z);
}

function beginKnockdownGround(f, ctx, emitter, hard) {
  f.knockdownHard = hard;
  f.groundTicks = 0;
  f.vx = 0;
  f.vz = 0;
  f.juggleCount = 0;
  f.wallSplatUsed = false;
  enterState(f, S.KNOCKDOWN_GROUND, ctx, emitter);
  emitter.cue('land-hard', f.x, f.y + 0.2, f.z, hard ? 1 : 0.75, 1, 0, 'wet-concrete');
}

// ---------------------------------------------------------------------------
// Intent resolution
// ---------------------------------------------------------------------------

/**
 * Reads the fighter's input snapshot and decides what they do this tick.
 *
 * Priority order matters and is deliberate:
 *   1. cancel/follow-up into a declared string continuation (highest — strings must feel tight)
 *   2. a new attack or throw from neutral
 *   3. stance / parry
 *   4. dashes and sidesteps (motion commands)
 *   5. guard
 *   6. crouch
 *   7. walk
 */
export function resolveIntent(f, other, ctx, emitter, cmdTable) {
  const snap = f.inputs;
  if (!snap) return;
  if (isNonCombatState(f.state)) return;
  if (f.frozenTicks > 0 || f.hitstunTicks > 0 || f.blockstunTicks > 0) return;
  if (f.throwPartner >= 0) return;

  const bufferTicks = Math.min(INPUT_BUFFER_TICKS, f60(ctx.config.get('inputBufferF60')));

  // --- 1. cancels / string continuations -------------------------------------------------
  if (f.move && f.moveId) {
    const m = f.move;
    const inWindow = f.moveTick >= m.tCancelOpen && f.moveTick <= m.tCancelClose;
    if (m.cancels.length > 0 && inWindow) {
      const next = findMove(cmdTable, snap, {
        candidates: m.cancels,
        stance: f.stance,
        crouching: f.crouching,
        airborne: f.airborne,
        bufferTicks,
        meter: f.meter,
      });
      if (next) {
        // A cancel is only permitted from a state the transition table allows, which stops a
        // whiffed startup from cancelling into a launcher.
        startMove(f, next, ctx, emitter);
        return;
      }
    }
    // While a move owns the fighter, nothing below may interrupt it.
    if (f.state !== S.ATTACK_RECOVERY || f.actionLockTicks > 0) return;
  }

  if (!canAct(f)) return;

  // --- 2. attacks and throws --------------------------------------------------------------
  const attack = findMove(cmdTable, snap, {
    candidates: null,
    stance: f.stance,
    crouching: f.crouching,
    airborne: f.airborne,
    bufferTicks,
    meter: f.meter,
  });
  if (attack) {
    startMove(f, attack, ctx, emitter);
    return;
  }

  // --- 3. movement commands ---------------------------------------------------------------
  if (snap.command === 'ff' && !f.crouching) {
    beginDash(f, ctx, emitter, 1);
    return;
  }
  if (snap.command === 'bb' && !f.crouching) {
    beginDash(f, ctx, emitter, -1);
    return;
  }
  if (snap.command === 'ss-up') {
    beginSidestep(f, ctx, emitter, 1);
    return;
  }
  if (snap.command === 'ss-down') {
    beginSidestep(f, ctx, emitter, -1);
    return;
  }

  // --- 4. guard ---------------------------------------------------------------------------
  // Holding back guards. This is the readable convention, and it means retreating is never
  // free — you are committing to a defensive posture.
  const wantsGuard = snap.back || snap.guard;
  const wantsCrouch = snap.down;
  if (wantsGuard) {
    // Changing guard height always costs the crouch transition frames. A fighter can never
    // teleport between a standing and a crouching guard, which is both what the transition
    // table declares and what makes low/high pressure a real threat rather than a formality.
    if (wantsCrouch && !f.crouching) {
      f.actionLockTicks = f60(f.stats.crouchEnterFrames);
      enterState(f, S.CROUCH_ENTER, ctx, emitter);
      f.vx = 0;
      f.vz = 0;
      return;
    }
    if (!wantsCrouch && f.crouching) {
      f.actionLockTicks = f60(f.stats.crouchExitFrames);
      enterState(f, S.CROUCH_EXIT, ctx, emitter);
      f.vx = 0;
      f.vz = 0;
      return;
    }
    const target = wantsCrouch ? S.GUARD_CROUCH : S.GUARD_STAND;
    if (f.state !== target) enterState(f, target, ctx, emitter);
    f.vx = snap.back && !snap.guard ? -f.stats.walkBackSpeed * f.facing * 0.55 : 0;
    f.vz = 0;
    return;
  }

  // --- 5. crouch --------------------------------------------------------------------------
  if (wantsCrouch) {
    if (!f.crouching) {
      f.actionLockTicks = f60(f.stats.crouchEnterFrames);
      enterState(f, S.CROUCH_ENTER, ctx, emitter);
    } else if (f.state === S.GUARD_CROUCH) {
      enterState(f, S.CROUCH, ctx, emitter);
    }
    f.vx = 0;
    f.vz = 0;
    return;
  }
  if (f.crouching && f.state !== S.CROUCH_EXIT) {
    f.actionLockTicks = f60(f.stats.crouchExitFrames);
    enterState(f, S.CROUCH_EXIT, ctx, emitter);
    return;
  }

  // --- 6. guard release -------------------------------------------------------------------
  if (f.guarding) {
    f.actionLockTicks = GUARD_RECOVERY_TICKS;
    enterState(f, S.GUARD_RECOVERY, ctx, emitter);
    return;
  }

  // --- 7. walking -------------------------------------------------------------------------
  const stanceWalkMul = f.stance === 'flow' ? f.stats.flowWalkMul
    : f.stance === 'anchor' ? f.stats.anchorWalkMul : 1;
  if (snap.fwd) {
    f.vx = f.stats.walkFwdSpeed * stanceWalkMul * f.facing;
    if (f.state !== S.WALK_FWD) enterState(f, S.WALK_FWD, ctx, emitter);
  } else if (snap.back) {
    f.vx = -f.stats.walkBackSpeed * stanceWalkMul * f.facing;
    if (f.state !== S.WALK_BACK) enterState(f, S.WALK_BACK, ctx, emitter);
  } else {
    f.vx = 0;
    if (f.state !== S.IDLE) enterState(f, S.IDLE, ctx, emitter);
  }

  // Lateral drift while walking lets a player creep off-axis without a full sidestep.
  const lateral = snap.up ? 1 : snap.down ? -1 : 0;
  f.vz = lateral * 0.45 * f.facing;
  void other;
}

/** True when the fighter is free to start a new action this tick. */
function canAct(f) {
  if (f.frozenTicks > 0) return false;
  if (f.hitstunTicks > 0 || f.blockstunTicks > 0) return false;
  if (f.actionLockTicks > 0) return false;
  if (f.down || f.state === S.KNOCKDOWN_FALL) return false;
  if (f.state === S.LANDING || f.state === S.HARD_LANDING) return false;
  if (f.move && f.state !== S.ATTACK_RECOVERY) return false;
  if (f.state === S.WALL_SPLAT || f.state === S.WALL_SLUMP) return false;
  if (f.throwPartner >= 0) return false;
  return true;
}

export function beginDash(f, ctx, emitter, dir) {
  const stats = f.stats;
  const frames = dir > 0 ? stats.dashFrames : stats.backdashFrames;
  f.actionLockTicks = f60(frames);
  f.vx = (dir > 0 ? stats.dashSpeed : -stats.backdashSpeed) * f.facing;
  f.vz = 0;
  if (dir < 0 && stats.backdashInvulnFrames > 0) {
    // Kestrel's backdash briefly ducks under throws and highs; Bulwark's does not.
    f.invulnTicks = f60(stats.backdashInvulnFrames);
    f.invulnLevels.length = 0;
    f.invulnLevels.push('throw', 'high');
  }
  enterState(f, dir > 0 ? S.DASH_FWD : S.DASH_BACK, ctx, emitter);
  emitter.cue('dash', f.x, f.y + 0.2, f.z, 0.7, dir > 0 ? 1 : 0.92, 0, 'wet-concrete');
}

export function beginSidestep(f, ctx, emitter, dir) {
  const stats = f.stats;
  f.actionLockTicks = f60(stats.sidestepFrames);
  // Local +Z is the fighter's left, so a sidestep "up" the screen depends on facing.
  f.vz = dir * stats.sidestepSpeed * f.facing;
  f.vx = 0;
  enterState(f, dir > 0 ? S.SIDESTEP_LEFT : S.SIDESTEP_RIGHT, ctx, emitter);
  emitter.cue('sidestep', f.x, f.y + 0.2, f.z, 0.6, 1, 0, 'wet-concrete');
}

/** Wake-up option selection from held inputs. Called when leaving KNOCKDOWN_GROUND. */
export function resolveWakeup(f, ctx, emitter, cmdTable) {
  const snap = f.inputs;
  const minTicks = f.knockdownHard ? GROUND_TICKS_HARD : GROUND_TICKS_SOFT;
  if (f.groundTicks < minTicks) return false;

  let option = 'neutral';
  if (snap) {
    if (snap.fwd || snap.back) option = 'roll';
    // A wake-up attack requires an attack button held through the get-up.
    if (snap.lp || snap.hp || snap.lk || snap.hk) option = 'attack';
  }

  f.wakeupOption = option;
  f.invulnTicks = WAKEUP_INVULN[option];
  f.invulnLevels.length = 0;

  if (option === 'attack') {
    const move = cmdTable ? findMove(cmdTable, snap, {
      candidates: null,
      stance: f.stance,
      crouching: false,
      airborne: false,
      bufferTicks: INPUT_BUFFER_TICKS,
      meter: f.meter,
    }) : null;
    enterState(f, S.WAKEUP_ATTACK, ctx, emitter);
    if (move) {
      f.move = move;
      f.moveId = move.id;
      f.moveTick = move.tStartup; // wake-up attacks skip startup: that is their whole appeal
      f.moveHasHit = false;
      f.hitboxUsed.fill(0);
    }
  } else if (option === 'roll') {
    f.actionLockTicks = f60(14);
    f.vx = (snap && snap.fwd ? 1 : -1) * 3.1 * f.facing;
    enterState(f, S.WAKEUP_ROLL, ctx, emitter);
  } else {
    f.actionLockTicks = f60(8);
    enterState(f, S.WAKEUP_NEUTRAL, ctx, emitter);
  }
  emitter.wakeup(f.index, option, f.invulnTicks);
  return true;
}

// ---------------------------------------------------------------------------
// Move progression
// ---------------------------------------------------------------------------

/**
 * Advances the active move by one tick and moves the fighter between the
 * startup / active / recovery sub-states. Also applies the move's own root motion.
 */
export function advanceMove(f, ctx, emitter) {
  const m = f.move;
  if (!m) return;
  if (f.frozenTicks > 0) return;
  if (f.hitstunTicks > 0 || f.blockstunTicks > 0) {
    // Being hit out of a move abandons it entirely.
    f.move = null;
    f.moveId = '';
    return;
  }

  const before = f.moveTick;
  f.moveTick++;
  const t = f.moveTick;

  // --- root motion: authored displacement spread across the startup window ---
  if (m.moveForward !== 0 && t <= m.tStartup && m.tStartup > 0) {
    f.vx = (m.moveForward / (m.tStartup * FIXED_STEP)) * f.facing;
  } else if (m.moveForward !== 0 && t === m.tStartup + 1) {
    f.vx = 0;
  }
  if (m.moveUp > 0 && before === 0) {
    f.vy = m.moveUp;
  }

  // --- armor / invulnerability windows ---
  if (m.armor.hits > 0) {
    const armed = t >= m.tArmorFrom && t < m.tArmorTo;
    if (!armed && f.armorHitsLeft > 0 && t >= m.tArmorTo) f.armorHitsLeft = 0;
  }
  if (m.invuln.levels.length > 0) {
    const armed = t >= m.tInvulnFrom && t < m.tInvulnTo;
    if (!armed && f.invulnLevels.length > 0) f.invulnLevels.length = 0;
    else if (armed && f.invulnLevels.length === 0) {
      for (const lv of m.invuln.levels) f.invulnLevels.push(lv);
    }
  }

  // --- sub-state progression ---
  const isThrowMove = m.isThrow;
  if (t === m.tStartup + 1) {
    if (isThrowMove) {
      // Throws stay in THROW_ATTEMPT until they connect; a whiffed throw is punishable.
    } else if (f.state === S.ATTACK_STARTUP) {
      enterState(f, S.ATTACK_ACTIVE, ctx, emitter);
      const p = emitter.begin('combat:active');
      p.fighter = f.index;
      p.moveId = m.id;
      p.hitboxIndex = 0;
      p.framesActive = m.active;
      emitter.send('combat:active');
    }
  }
  if (t === m.tStartup + m.tActive + 1) {
    if (!f.moveHasHit && !isThrowMove) {
      emitter.whiff(f.index, m.id, m.recovery);
      logAction(f, ctx.time.tick, m.id, 'whiff');
    }
    if (f.state === S.ATTACK_ACTIVE || f.state === S.THROW_ATTEMPT || f.state === S.AIR_ATTACK) {
      enterState(f, f.airborne ? S.AIRBORNE : S.ATTACK_RECOVERY, ctx, emitter);
    }
  }

  // --- move end ---
  if (t >= m.tTotal) {
    const landed = f.airborne ? S.AIRBORNE : S.IDLE;
    endMove(f, ctx, emitter, landed);
  }
}

/**
 * Applies a stance change or parry from a `stance`/`defence` category move.
 * Called by the combat system when a stance move reaches its active window.
 */
export function applyStanceMove(f, ctx, emitter) {
  const m = f.move;
  if (!m) return;
  if (m.category === 'defence' && f.stats.parryWindowFrames > 0) {
    f.actionLockTicks = f60(f.stats.parryWindowFrames);
    f.parryTicks = f60(f.stats.parryWindowFrames);
    f.parrySucceeded = false;
    enterState(f, S.PARRY_ACTIVE, ctx, emitter);
    f.move = null;
    f.moveId = '';
    return;
  }
  if (m.category === 'stance') {
    f.stance = m.stanceEnter || (f.stance ? '' : f.stats.stances[1]);
    f.stanceTicks = 0;
    addMeter(f, 0);
  }
}

/** Successful parry: refunds the frame advantage the fighter's stats promise. */
export function applyParrySuccess(f, attacker, ctx, emitter) {
  f.parrySucceeded = true;
  f.parryTicks = 0;
  f.actionLockTicks = 0;
  addMeter(f, f.stats.meterOnParry);
  enterState(f, S.PARRY_SUCCESS, ctx, emitter);
  // The attacker eats the advantage as extra recovery, which is what makes a parry a read
  // rather than a free block.
  attacker.actionLockTicks = Math.max(attacker.actionLockTicks, f60(f.stats.parryAdvantageFrames));
  const p = emitter.begin('combat:parry');
  p.defender = f.index;
  p.attacker = attacker.index;
  p.moveId = attacker.moveId;
  p.window = f.stats.parryWindowFrames;
  p.frameAdvantage = f.stats.parryAdvantageFrames;
  emitter.send('combat:parry');
  emitter.cue('parry', f.x, f.y + 1.1, f.z, 1, 1, 0);
  emitter.impact('parry-ring', f.x + f.facing * 0.35, f.y + 1.15, f.z, -f.facing, 0, 0, 1);
  emitter.cameraImpulse('parry', 0.35, -f.facing, 0.2, 0, f60(6), 0, -0.02);
}

/** Ends a combo when enough ticks have passed since the last connected hit. */
export function checkComboEnd(f, other, ctx, emitter) {
  if (f.comboHits === 0) return;
  const sinceHit = ctx.time.tick - f.lastHitTick;
  const opponentFree = !other.inHitstun && !other.down && other.throwPartner < 0
    && other.state !== S.WALL_SPLAT && other.state !== S.WALL_SLUMP;
  if (sinceHit > f60(30) || opponentFree) {
    emitter.comboEnd(f.index, other.index, f.comboHits, f.comboDamage, f.comboScale, f.comboWallUsed);
    resetCombo(f);
  }
}

/** Clamps a fighter's velocities to sane bounds so a data error cannot fling them off-stage. */
export function clampVelocity(f) {
  f.vx = clamp(f.vx, -18, 18);
  f.vy = clamp(f.vy, -26, 18);
  f.vz = clamp(f.vz, -8, 8);
}

export { LAND_TICKS, HARD_LAND_TICKS, GROUND_TICKS_SOFT, GROUND_TICKS_HARD, THROW_BREAK_TICKS };
